import {memo} from 'react'
import {NotificationType, UserNotification} from '@/features/notifications/types'
import {StyledNotificationItem, StyledNotificationTime, StyledNotificationUnreadDot} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {useMarkNotificationAsRead} from '@/features/notifications/queries/useMarkNotificationAsRead'
import {formatNotificationDate} from '@/features/notifications/utils/dateFormatters'
import {useNavigate} from 'react-router-dom'
import {Bell01Icon, CheckDone01Icon, MessageChatSquareIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {useMarkNotification} from '../../hooks/useMarkNotification'
import {useTranslation} from '@/translations/i18n'

interface NotificationItemProps {
    notification: UserNotification
    onClick?: (notification: UserNotification) => void
}

const NotificationItemComponent = ({notification, onClick}: NotificationItemProps) => {
    const {t} = useTranslation()
    const {mutate: markAsRead} = useMarkNotificationAsRead()
    const {mutate: markNotification} = useMarkNotification({urlParams: {notificationId: notification.id}})
    const navigate = useNavigate()

    const {title, type, severity, sentAt, extraData, triggeredByUser, link, bodyTemplate} = notification.notification

    const formattedTime = formatNotificationDate(sentAt)

    const handleClick = () => {
        if (!notification.readAt) {
            markAsRead(notification.id)
        }

        if (onClick) {
            onClick(notification)
        }

        if (link) {
            const url = new URL(link)
            navigate(`${url.pathname}${url.search}${url.hash}`)
        }
    }

    return (
        <StyledNotificationItem
            $isAlert={severity === 'alert'}
            direction="row"
            align="start"
            justify="space-between"
            fullWidth
            gap={4}
            onClick={handleClick}
        >
            <Flexbox direction="column" align="start" gap={2}>
                <Flexbox direction="row" align="start" gap={2}>
                    {!notification.readAt && <StyledNotificationUnreadDot />}
                    <Flexbox direction="column" align="start" gap={2}>
                        {triggeredByUser && (
                            <Flexbox direction="row" align="center" gap={2}>
                                <Avatar name={`${triggeredByUser.firstName} ${triggeredByUser.lastName}`} size="xs" />
                                <Text size="textXs" palette="neutral" shade="700">
                                    {`${triggeredByUser.firstName} ${triggeredByUser.lastName}`}
                                </Text>
                            </Flexbox>
                        )}
                        <Flexbox direction="column" gap={1}>
                            <Flexbox align="center" gap={1}>
                                {notification.notification.type === NotificationType.Enum['Conv-001'] ||
                                notification.notification.type === NotificationType.Enum['Conv-002'] ? (
                                    <MessageChatSquareIcon size={16} />
                                ) : (
                                    <Bell01Icon size={16} />
                                )}
                                <Text size="textMd" weight="500" palette="neutral" shade="900">
                                    {title}
                                </Text>
                            </Flexbox>
                            {extraData && (
                                <Text size="textXs" palette="neutral" shade="700">
                                    {extraData.patientName && <span>{extraData.patientName}</span>}
                                    {extraData.projectCode && <span> • PRI n°{extraData.projectCode}</span>}
                                    {type === 'Age-001-Repeated' && <span> • {bodyTemplate}</span>}
                                </Text>
                            )}
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
            <Flexbox direction="column" align="end" gap={1}>
                <Button
                    variant={notification.readAt ? 'primaryNeutral' : 'secondary'}
                    size="xs"
                    shape="rectangle"
                    onClick={event => {
                        event.stopPropagation()
                        markNotification({isRead: !notification.readAt})
                    }}
                >
                    <CheckDone01Icon />
                    {notification.readAt ? t('notifications:mark_as_unread') : t('notifications:mark_as_read')}
                </Button>
                <StyledNotificationTime>{formattedTime}</StyledNotificationTime>
            </Flexbox>
        </StyledNotificationItem>
    )
}

export const NotificationItem = memo(NotificationItemComponent)
